import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Loader2, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Product configuration with external URLs
const PRODUCT_CONFIG = {
  hr: {
    name: 'MyHR Orbit',
    baseUrl: 'https://myhrorbit.com',
    dashboardPath: '/dashboard',
    color: 'blue'
  },
  finance: {
    name: 'MyFinance Orbit', 
    baseUrl: 'https://myfinanceorbit.com',
    dashboardPath: '/dashboard',
    color: 'emerald'
  },
  docs: {
    name: 'MyDoc Orbit',
    baseUrl: 'https://mydocorbit.com', 
    dashboardPath: '/dashboard',
    color: 'purple'
  },
  sales: {
    name: 'MySales Orbit',
    baseUrl: 'https://brand-logos-deploy.preview.emergentagent.com',
    dashboardPath: '/dashboard',
    color: 'orange'
  }
};

export default function ProductEmbed() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('');

  const product = PRODUCT_CONFIG[productId];

  useEffect(() => {
    if (!product) {
      setError('Product not found');
      setLoading(false);
      return;
    }

    const initializeEmbed = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }

        // Generate SSO token for the target app
        const response = await axios.post(
          `${API}/sso/generate`,
          { target_app: productId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { sso_token } = response.data;
        
        // Construct the iframe URL with SSO token
        const targetUrl = `${product.baseUrl}${product.dashboardPath}#sso_token=${sso_token}`;
        setIframeUrl(targetUrl);
        setLoading(false);

      } catch (err) {
        console.error('SSO Error:', err);
        if (err.response?.status === 403) {
          setError('You do not have access to this product');
        } else {
          setError('Failed to initialize product. Please try again.');
        }
        setLoading(false);
      }
    };

    initializeEmbed();
  }, [productId, product, navigate]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setError('Failed to load the application');
    setLoading(false);
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      setLoading(true);
      iframeRef.current.src = iframeUrl;
    }
  };

  const handleOpenExternal = () => {
    window.open(iframeUrl, '_blank');
  };

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Product Not Found</h2>
        <p className="text-slate-400 mb-4">The requested product does not exist.</p>
        <Button onClick={() => navigate('/portal')} variant="outline">
          Back to Portal
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Unable to Load {product.name}</h2>
        <p className="text-slate-400 mb-4">{error}</p>
        <div className="flex gap-3">
          <Button onClick={handleRefresh} variant="outline" className="border-slate-600 text-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          <Button onClick={() => navigate('/portal')} variant="outline" className="border-slate-600 text-slate-300">
            Back to Portal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 rounded-t-lg border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full bg-${product.color}-500`} />
          <span className="text-sm font-medium text-white">{product.name}</span>
          {loading && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleRefresh}
            className="text-slate-400 hover:text-white h-8"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleOpenExternal}
            className="text-slate-400 hover:text-white h-8"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="flex-1 flex items-center justify-center bg-slate-900 rounded-b-lg">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-slate-400">Loading {product.name}...</p>
          </div>
        </div>
      )}

      {/* Iframe */}
      {iframeUrl && (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className={`flex-1 w-full border-0 rounded-b-lg bg-slate-900 ${loading ? 'hidden' : ''}`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          allow="clipboard-read; clipboard-write; camera; microphone"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
          title={product.name}
        />
      )}
    </div>
  );
}
